/**
 * `AccountSwitcher` — TopBar dropdown for agency users (ADR-0237 §6).
 *
 * Visible when:
 *   - `features.agency === true` (the home session is agency-enabled)
 *     AND the dashboard is NOT already acting, OR
 *   - the dashboard IS acting (so the user can see "where am I" + a
 *     one-click return to the agency).
 *
 * UX pass 2026-10-11:
 *   - Trigger button has a FIXED width + ellipsis so a long client
 *     name does not resize the TopBar when the user switches.
 *   - Menu opens with a simple opacity fade (no y-translate), to kill
 *     the "button jumps" flicker the founder reported.
 *   - Clients are listed as `{name}` + `{domain}` with a check on the
 *     currently-acting one. When the client's `name` collides with
 *     the agency's own `name` (common when both inboxes share an
 *     email domain → the server's `defaultAccountName` returns the
 *     same word for both accounts), the primary domain is promoted
 *     to the primary label (see `client-label.ts`).
 *   - When acting, the top line reads "VIEWING CLIENT" + the client's
 *     label; a dedicated "Back to {Agency}" row (with ArrowLeft) is
 *     the single, obvious release path. No "via X" jargon.
 *   - Clients list cache survives opens: `loadClients` is called once
 *     on first open AND whenever the acting state transitions; a
 *     background refresh runs on every subsequent open so stale rows
 *     update without re-rendering a blank list.
 *
 * The server is the single source of truth for the acting state
 * (`User.acting_context` on `/v1/auth/me`); the switcher reads from
 * the AgencyProvider's `acting` field and never persists anything
 * client-side.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, Briefcase, Check, ChevronsUpDown, Users } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ClientListItemWire } from '@sill/types';

import { useOptionalHttp } from '../../api/http-context.js';
import { useAgency } from '../../app/agency-context.js';
import { RELEASE_LANDING_PATH, actAsClient, listClients, releaseActingWithRetry } from '../../api/agency.js';
import { useAuth } from '../../app/auth-context.js';
import { useC } from '../../lib/theme.js';
import { Tooltip } from '../atoms/index.js';
import { labelForClient } from './client-label.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

/** Fixed trigger width so a long client / agency name does not
 *  resize the TopBar when the user switches between accounts. */
const TRIGGER_WIDTH = 220;

export interface AccountSwitcherProps {
  /** Called when the switcher triggers a full reload (success case).
   *  Defaults to `window.location.assign('/')`. Overridable for tests. */
  onReload?: () => void;
  /** Mobile drawer: full-width trigger, list opens in place (no
   *  floating dropdown that would overflow the drawer). */
  inline?: boolean;
  /**
   * Density mode (founder 2026-10-20): on agency accounts the trigger's
   * full text form eats so much room that the site tabs fall into
   * "More" on normal viewports. The TopBar flips `compact` on when
   * squeeze is needed:
   *   - 'full'     — icon + CLIENT kicker + name (default)
   *   - 'ellipsis' — same shape, name truncated to a tight max width
   *   - 'icon'     — icon + chevron only, name hidden; the full label
   *                  rides on `aria-label` + a Tooltip so screen readers
   *                  and hover still disclose it. The dropdown header
   *                  still carries the full name so the user never loses
   *                  context on open.
   */
  density?: 'full' | 'ellipsis' | 'icon';
}

export function AccountSwitcher({
  onReload,
  inline = false,
  density = 'full',
}: AccountSwitcherProps): JSX.Element | null {
  const C = useC();
  const auth = useAuth();
  const http = useOptionalHttp();
  const { features, acting } = useAgency();
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState<readonly ClientListItemWire[] | null>(null);
  const [clientsErr, setClientsErr] = useState<string | null>(null);
  const [switching, setSwitching] = useState<string | null>(null);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const user = auth.state.kind === 'authenticated' ? auth.state.user : null;
  const show =
    user !== null
    && ((features?.agency === true && acting === null) || acting !== null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (containerRef.current === null) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onDocClick);
    window.addEventListener('keydown', onEsc);
    return () => {
      window.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const loadClients = useCallback(
    async (opts: { silent?: boolean } = {}): Promise<void> => {
      if (http === null) return;
      // UX fix 2026-10-11 — a background refresh on later opens never
      // blanks the list (we don't null `clients` here). The initial
      // load clears the error so a transient failure doesn't stick.
      if (opts.silent !== true) setClientsErr(null);
      const r = await listClients(http);
      if (r.kind === 'ok') {
        setClients(r.clients);
        if (opts.silent === true) setClientsErr(null);
      } else if (r.kind === 'forbidden') {
        if (opts.silent !== true) setClients([]);
        setClientsErr('Agency access is not available on this account.');
      } else {
        if (opts.silent !== true) setClientsErr(r.message);
      }
    },
    [http],
  );

  // First open fetches; later opens refresh silently (keep the cached
  // list visible). We also refetch on acting-state transitions so the
  // "✓ current" marker tracks the server's authoritative state.
  useEffect(() => {
    if (!open) return;
    if (clients === null) {
      void loadClients();
    } else {
      void loadClients({ silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // When the acting state transitions (switch or release), drop the
  // cache so the next open fetches a fresh list. Cheap compared to the
  // visual jitter of an out-of-date "current" marker.
  useEffect(() => {
    setClients(null);
  }, [acting?.acting_account_id]);

  const doSwitch = async (client_account_id: string): Promise<void> => {
    if (http === null || switching !== null || user === null) return;
    setSwitching(client_account_id);
    setSwitchError(null);
    const r = await actAsClient(http, client_account_id);
    if (r.kind === 'ok') {
      // Refresh the AuthProvider so the next /me carries the new
      // acting_context; the full-reload path then renders the shell
      // under the acting state.
      try {
        await auth.refresh();
      } catch {
        /* a refresh race here just falls through to the reload. */
      }
      if (onReload !== undefined) {
        onReload();
      } else if (typeof window !== 'undefined') {
        // Land on the client's own dashboard (its sites). The Agency
        // Portal is unavailable while acting, so landing there was a
        // dead end (founder 2026-10-11).
        window.location.assign('/?view=sites');
      }
      return;
    }
    if (r.kind === 'not_found') {
      setSwitchError('That client is no longer linked. Refresh to update the list.');
    } else if (r.kind === 'forbidden') {
      setSwitchError(humanizeForbidden(r.reason));
    } else {
      setSwitchError(r.message);
    }
    setSwitching(null);
  };

  const doRelease = async (): Promise<void> => {
    if (http === null || switching !== null) return;
    setSwitching('__release__');
    setSwitchError(null);
    const r = await releaseActingWithRetry(http);
    if (r.kind === 'ok') {
      try {
        await auth.refresh();
      } catch {
        /* refresh race → the reload below falls through cleanly. */
      }
      if (onReload !== undefined) {
        onReload();
      } else if (typeof window !== 'undefined') {
        // Founder 2026-10-28: back home lands on All sites, not the
        // Agency Portal.
        window.location.assign(RELEASE_LANDING_PATH);
      }
      return;
    }
    setSwitching(null);
    setSwitchError(`Could not return to ${home_name}. ${r.message}`);
  };

  // The HOME account's display name — always the agency's own name on
  // an agency user's session (see `/v1/auth/me` `home_account_name`
  // extension). Falls back to the user's own name, then to the raw id,
  // so the trigger always has SOMETHING to render.
  const home_name =
    (user?.home_account_name !== undefined && user.home_account_name.length > 0)
      ? user.home_account_name
      : (user !== null && user.name.length > 0
        ? user.name
        : (user?.account_id ?? ''));

  // ADR-0237 §6 — when acting, the trigger shows the CLIENT's label.
  // We prefer the primary domain over the raw name when the two
  // accounts share a defaultAccountName (see `labelForClient`).
  const acting_client_sibling = useMemo(() => {
    if (acting === null || clients === null) return null;
    return clients.find((c) => c.client_account_id === acting.acting_account_id) ?? null;
  }, [acting, clients]);

  const acting_label = useMemo<string>(() => {
    if (acting === null) return '';
    if (acting_client_sibling !== null) {
      return labelForClient(acting_client_sibling, home_name).primary;
    }
    // Fallback to the server's `acting_context` name (the one on
    // `/v1/auth/me`), applying the same preference rule inline — we
    // don't have a sibling row yet but we DO know the agency's name
    // and the acting account's name.
    const raw_name = acting.acting_account_name;
    if (raw_name.trim().length === 0) return acting.acting_account_id;
    if (
      home_name.length > 0
      && raw_name.trim().toLowerCase() === home_name.trim().toLowerCase()
    ) {
      // No sibling row + colliding names → still show the raw name so
      // the trigger is never blank; the dropdown lists the client with
      // the domain for disambiguation once the list loads.
      return raw_name;
    }
    return raw_name;
  }, [acting, acting_client_sibling, home_name]);

  if (!show || user === null) return null;

  // Density-driven trigger width. The icon-only form stays wide enough
  // for the Briefcase + chevron (no text) + a comfortable hit target.
  // Full / ellipsis share the TRIGGER_WIDTH ceiling but ellipsis caps
  // the inner text more aggressively so the trigger shrinks before the
  // nav tabs fall into "More" (founder 2026-10-20).
  const triggerMaxWidth = inline
    ? undefined
    : density === 'icon'
      ? 56
      : density === 'ellipsis'
        ? 140
        : TRIGGER_WIDTH;
  const triggerWidth = inline
    ? '100%'
    : density === 'icon'
      ? '56px'
      : `min(${triggerMaxWidth}px, 44vw)`;
  const activeTriggerLabel = acting !== null ? acting_label : home_name;
  const ariaLabel = acting !== null
    ? `Switch account · viewing client ${activeTriggerLabel}`
    : `Switch account · ${activeTriggerLabel}`;

  const triggerButton = (
    <button
      type="button"
      onClick={() => setOpen((v) => !v)}
      className="flex items-center gap-2 px-2 sm:px-3 py-1.5"
      aria-expanded={open}
      aria-label={ariaLabel}
      data-testid="account-switcher-trigger"
      data-density={density}
      style={{
        background: C.surface2,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        // Fixed width + overflow so a long client / agency name
        // does not resize the TopBar when the user switches.
        // Shrinks on phones (founder 2026-10-11: switcher was hidden
        // on mobile) while staying fixed per viewport so it never jumps.
        width: triggerWidth,
        minWidth: 0,
        maxWidth: triggerMaxWidth,
        justifyContent: 'space-between',
      }}
    >
      <span className="flex items-center gap-2 min-w-0">
        <Briefcase size={11} color={acting !== null ? C.sky : C.muted} />
        {density !== 'icon' && acting !== null ? (
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              letterSpacing: 2,
              color: C.sky,
              textTransform: 'uppercase',
              flex: '0 0 auto',
            }}
          >
            CLIENT
          </span>
        ) : null}
        {density !== 'icon' ? (
          <span
            title={activeTriggerLabel} /* title-ok: full value of the ellipsised account name */
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {activeTriggerLabel}
          </span>
        ) : null}
      </span>
      <ChevronsUpDown size={11} color={C.muted} />
    </button>
  );

  return (
    <div ref={containerRef} className="relative block">
      {density === 'icon' && !inline ? (
        <Tooltip content={activeTriggerLabel} side="bottom">
          {triggerButton}
        </Tooltip>
      ) : (
        triggerButton
      )}
      <AnimatePresence>
        {open && (
          <motion.div
            // UX fix 2026-10-11 — simple opacity fade. Dropping the
            // y-translate kills the "menu jumps" effect the founder
            // reported.
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            className={inline ? 'mt-2' : 'absolute top-full left-0 mt-2 z-50'}
            data-testid="account-switcher-menu"
            style={{
              width: inline ? '100%' : 'min(320px, calc(100vw - 24px))',
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 3,
              boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            }}
          >
            {acting !== null ? (
              <ActingHeader
                C={C}
                client_label={acting_label}
                client_sibling={acting_client_sibling}
                home_name={home_name}
                acting_account_name={acting.acting_account_name}
                acting_account_id={acting.acting_account_id}
              />
            ) : (
              <HomeHeader C={C} home_name={home_name} />
            )}

            {acting !== null ? (
              <button
                type="button"
                data-testid="account-switcher-back-to-agency"
                disabled={switching !== null}
                onClick={() => void doRelease()}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left"
                style={{
                  background: 'transparent',
                  borderTop: `1px solid ${C.borderSoft}`,
                  borderBottom: `1px solid ${C.borderSoft}`,
                  cursor: switching !== null ? 'default' : 'pointer',
                }}
              >
                <ArrowLeft size={14} color={C.text} />
                <div className="flex-1 min-w-0">
                  <div
                    style={{
                      fontFamily: sansFont,
                      fontSize: 13,
                      color: C.text,
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Back to {home_name}
                  </div>
                </div>
                {switching === '__release__' ? (
                  <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>…</span>
                ) : null}
              </button>
            ) : null}

            <ListKicker
              C={C}
              label={acting !== null ? 'OTHER CLIENTS' : 'CLIENTS'}
              count={
                clients === null
                  ? '…'
                  : acting === null
                    ? clients.length
                    // When acting, the current client is rendered in
                    // the header above — exclude it from the "other"
                    // count below so the label is honest.
                    : clients.filter((c) => c.client_account_id !== acting.acting_account_id).length
              }
            />
            {clientsErr !== null && clients === null ? (
              <div
                className="px-3 py-3"
                style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg }}
              >
                {clientsErr}
              </div>
            ) : null}
            {clients !== null && clients.length === 0 ? (
              <div
                className="px-3 py-3"
                style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}
              >
                No linked clients yet. Open <strong style={{ color: C.text }}>Clients</strong> to add one.
              </div>
            ) : null}
            {clients !== null
              && clients
                // Hide the currently-acting client from the sibling
                // list; it's already rendered in the acting header
                // above (removing the visual duplication the first
                // UX pass left in).
                .filter((c) => acting === null || c.client_account_id !== acting.acting_account_id)
                .map((c) => {
                const active_link = c.link_status === 'active';
                const is_current_acting =
                  acting !== null && acting.acting_account_id === c.client_account_id;
                // Disable rows for the currently-acting client
                // (nothing to switch into) and for pending_owner
                // clients (the switch would land in an unclaimed
                // account whose owner-user doesn't exist yet).
                const can_switch =
                  active_link
                  && !is_current_acting
                  && c.owner_claim_status !== 'pending_owner';
                const label = labelForClient(c, home_name);
                return (
                  <button
                    key={c.link_id}
                    type="button"
                    disabled={!can_switch || switching !== null}
                    data-testid={`account-switcher-client-${c.client_account_id}`}
                    onClick={() => void doSwitch(c.client_account_id)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left"
                    style={{
                      background: is_current_acting ? C.surface2 : 'transparent',
                      borderBottom: `1px solid ${C.borderSoft}`,
                      opacity: can_switch || is_current_acting ? 1 : 0.6,
                      cursor: can_switch ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {is_current_acting ? (
                      <Check size={13} color={C.limeFg} />
                    ) : (
                      <Users size={13} color={C.muted} />
                    )}
                    <div className="flex-1 min-w-0">
                      <div
                        style={{
                          fontFamily: sansFont,
                          fontSize: 13,
                          color: C.text,
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {label.primary}
                      </div>
                      {label.secondary !== null || !active_link || c.owner_claim_status === 'pending_owner' ? (
                        <div
                          style={{
                            fontFamily: monoFont,
                            fontSize: 10,
                            color: C.muted,
                            marginTop: 2,
                          }}
                        >
                          {label.secondary ?? ''}
                          {c.owner_claim_status === 'pending_owner'
                            ? (label.secondary !== null ? ' · ' : '') + 'awaiting owner'
                            : c.link_status === 'suspended'
                              ? (label.secondary !== null ? ' · ' : '') + 'suspended'
                              : ''}
                        </div>
                      ) : null}
                    </div>
                    {switching === c.client_account_id ? (
                      <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>…</span>
                    ) : null}
                  </button>
                );
              })}
            {switchError !== null ? (
              <div
                className="px-3 py-2"
                style={{
                  fontFamily: sansFont,
                  fontSize: 12,
                  color: C.coralFg,
                  borderTop: `1px solid ${C.borderSoft}`,
                }}
              >
                {switchError}
              </div>
            ) : null}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ListKicker({
  C,
  label,
  count,
}: {
  C: ReturnType<typeof useC>;
  label: string;
  count: number | string;
}): JSX.Element {
  return (
    <div
      className="px-3 py-2"
      style={{
        fontFamily: monoFont,
        fontSize: 9,
        color: C.muted,
        letterSpacing: 2,
        borderTop: `1px solid ${C.borderSoft}`,
        borderBottom: `1px solid ${C.borderSoft}`,
      }}
    >
      {label} · {count}
    </div>
  );
}

function HomeHeader({ C, home_name }: { C: ReturnType<typeof useC>; home_name: string }): JSX.Element {
  return (
    <>
      <div
        className="px-3 py-2"
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 2,
          borderBottom: `1px solid ${C.borderSoft}`,
        }}
      >
        YOUR AGENCY
      </div>
      <div
        className="w-full flex items-center gap-3 px-3 py-2.5"
        style={{ background: C.surface2 }}
      >
        <Briefcase size={13} color={C.skyFg} />
        <div className="flex-1 min-w-0">
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {home_name}
          </div>
        </div>
      </div>
    </>
  );
}

function ActingHeader({
  C,
  client_label,
  client_sibling,
  home_name,
  acting_account_name,
  acting_account_id,
}: {
  C: ReturnType<typeof useC>;
  client_label: string;
  client_sibling: ClientListItemWire | null;
  home_name: string;
  acting_account_name: string;
  acting_account_id: string;
}): JSX.Element {
  // The secondary line shows the OTHER identity (name or domain)
  // whichever is not already in `client_label`. When client_sibling
  // is null (list not loaded yet), surface the raw acting_account
  // name as a fallback.
  let secondary: string | null = null;
  if (client_sibling !== null) {
    const label = labelForClient(client_sibling, home_name);
    secondary = label.secondary;
  } else if (acting_account_name.trim().length > 0 && acting_account_name !== client_label) {
    secondary = acting_account_name;
  } else if (acting_account_name.trim().length === 0) {
    secondary = acting_account_id;
  }
  return (
    <>
      <div
        className="px-3 py-2"
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 2,
          borderBottom: `1px solid ${C.borderSoft}`,
        }}
      >
        VIEWING CLIENT
      </div>
      <div
        className="w-full flex items-center gap-3 px-3 py-2.5"
        style={{ background: C.surface2 }}
      >
        <Briefcase size={13} color={C.skyFg} />
        <div className="flex-1 min-w-0">
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            data-testid="account-switcher-acting-primary"
          >
            {client_label}
          </div>
          {secondary !== null ? (
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.muted,
                marginTop: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {secondary}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}

function humanizeForbidden(reason: string): string {
  switch (reason) {
    case 'agency_feature_disabled':
      return 'The agency feature is not enabled on this account.';
    case 'role_insufficient':
      return 'Only owners and admins on an agency account can switch into a client.';
    case 'acting_session_denied':
      return 'Return to your home account before switching into another client.';
    case 'region_mismatch':
      return 'This client is in a different region.';
    default:
      return 'Access denied.';
  }
}
